/**
 * Sentry Error Monitoring - Frontend
 */

import * as Sentry from '@sentry/browser';

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('[Sentry] No VITE_SENTRY_DSN configured');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    // Only send errors in production
    enabled: import.meta.env.PROD,
    beforeSend(event) {
      // Filter out noisy errors
      if (event.message?.includes('ResizeObserver')) {
        return null;
      }
      return event;
    },
  });
  
  initialized = true;
}

/**
 * Capture an exception with context
 */
export function captureException(
  error: Error | unknown, 
  context?: { 
    userId?: string; 
    action?: string; 
    extra?: Record<string, any>;
  }
): void {
  console.error('[Error]', context?.action || 'unknown', error);
  
  if (!initialized) return;

  Sentry.withScope((scope) => {
    if (context?.userId) {
      scope.setUser({ id: context.userId });
    }
    if (context?.action) {
      scope.setTag('action', context.action);
    }
    if (context?.extra) {
      scope.setExtras(context.extra);
    }
    
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), 'error');
    }
  });
}

/**
 * Set user context (call after login)
 */
export function setUser(userId: string, email?: string): void {
  if (!initialized) return;
  Sentry.setUser({ id: userId, email });
}

/**
 * Clear user context (call after logout)
 */
export function clearUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}
